import { calculateBackoffWithJitter } from '@federa/const/backoff-math';
import { safeJsonParse } from '@federa/utils/json/safe-json';
import { isPlainObject } from '@federa/const/record-guards';
import { extractError } from '@federa/utils/errors/extract-error';
import { getEnvironmentConfig } from '@federa/components/lib/environment';
import { DEFAULT_CONVERSATION_TYPE } from '@federa/const/assistant';
import { MAX_DESCRIPTION_LENGTH } from '@federa/const/observability';
import { ROOM_API_ROUTES } from '@federa/const/routes-api';
import { WEBSOCKET_RETRY_CONFIG } from '@federa/const/timing';
import { WS_ACTIONS } from '@federa/const/websocket-actions';
import { LOGGER_TAGS } from '@federa/const/logging';
import { buildClientSubprotocols } from '@federa/utils/websocket/ws-client-subprotocols';
import { isWebSocketConnected } from '@federa/utils/websocket/is-web-socket-connected';
import { respondPong, sendWsMessage } from '@federa/utils/websocket/send';
import { scopedLogger } from '@federa/utils/deps/registry';
import { readGuestSessionTokenClaimsUnverified } from '@federa/utils/security/guest-session-token';
import { ERROR } from '@federa/const/error-values';
import { resolveWsCredential } from './ws-credential.ts';
import {
	handleHistoryArray,
	handleMessagesPayload,
	handleParticipantsPayload,
	handleJoinEvent,
	handleQuitEvent,
	handleChatMessage,
	handleAgentStatus,
} from './messaging-client.handlers.ts';
import type { WsOutboundFrame } from '@federa/types/api-websocket';
import type { ChatMessageStruct, ChatParticipantStruct } from '@federa/types/conversations-chat-structs';
import type {
	ChatFileAttachment as FileAttachment,
	MessagingCallbacks,
	MessagingClientHandle,
	MessagingMessageHandlerContext as MessageHandlerContext,
} from '@federa/types/ui-chat-messaging';

const log = scopedLogger(LOGGER_TAGS.MESSAGING_CLIENT);

interface MessagingClientOptions extends MessagingCallbacks<ChatMessageStruct, ChatParticipantStruct> {
	userId: string;
	username: string;
	role: string;
	roomId: string;
	conversationType?: string;
	onConnectionChange?: (connected: boolean) => void;
	shouldReconnect?: boolean;
	/** Absolute ws(s) base for the ChatRoom DO. The chats SPA passes this because the admin shell's env config carries no absolute chatRoomWS; defaults to `getEnvironmentConfig().chatRoomWS`. */
	wsUrlBase?: string;
}

// Message handling helpers in messaging-client.handlers.ts

/** The socket's message listener: one shape for the handler the state HOLDS and the handler the factory hands back,
 *  so the two cannot drift and leave a listener that is registered but never the one that was built. */
type WsMessageListener = (event: MessageEvent) => void;

interface WebSocketState {
	socket: WebSocket | null;
	/** The subject THIS socket authenticated as -- the credential's own `gid` for a guest, else the caller's id. Every
	 *  frame that names a user must use it: the DO compares the handshake's `userId` against the subject it verified at
	 *  upgrade and closes on `UserId mismatch`, so a frame built from app state can be closed by an id that changed
	 *  after the socket opened. Null until the first connect resolves it. */
	subjectUserId: string | null;
	closed: boolean;
	reconnectAttempts: number;
	reconnectTimeoutId: number | null;
	handleOpen: (() => void) | null;
	handleMessage: WsMessageListener | null;
	handleClose: (() => void) | null;
	handleError: ((event: Event) => void) | null;
}

function createOpenHandler(state: WebSocketState, options: MessagingClientOptions): () => void {
	return () => {
		state.reconnectAttempts = 0;
		options.onConnectionChange?.(true);
		const handshake = {
			name: options.username,
			userId: state.subjectUserId ?? options.userId,
			role: options.role,
			roomId: options.roomId,
			conversationType: options.conversationType ?? '',
			type: 'chatMessages',
			sendOnHandshake: true,
			firstLoad: true,
		};
		if (state.socket) {
			void sendWsMessage(state.socket, handshake);
		}
	};
}

/** Type guard for a decoded WS frame: either one record, or a batch of records (the two shapes this handler branches on). Anything else -- a scalar, a mixed array -- is an unusable frame and takes the parse-failure path. */
function isWsFramePayload(value: unknown): value is Record<string, unknown> | Record<string, unknown>[] {
	return Array.isArray(value) ? value.every(isPlainObject) : isPlainObject(value);
}

function createMessageHandler(state: WebSocketState, options: MessagingClientOptions): WsMessageListener {
	return (event: MessageEvent) => {
		const data = safeJsonParse(String(event.data), null, { guard: isWsFramePayload });
		if (!data) {
			log.debug(ERROR.FAILED_TO_PARSE_WEB_SOCKET_MESSAGE, {
				dataPreview: typeof event.data === 'string' ? event.data.slice(0, MAX_DESCRIPTION_LENGTH) : 'non-string data',
			});
			return;
		}

		if (!Array.isArray(data) && data.action === WS_ACTIONS.PING) {
			if (state.socket) {
				void respondPong(state.socket);
			}
			return;
		}

		const ctx: MessageHandlerContext = {
			roomId: options.roomId,
			onHistory: options.onHistory,
			onParticipants: options.onParticipants,
			onParticipantJoin: options.onParticipantJoin,
			onParticipantLeave: options.onParticipantLeave,
			onMessage: options.onMessage,
			onAgentStatus: options.onAgentStatus,
		};

		if (Array.isArray(data)) {
			void handleHistoryArray(data, ctx);
			return;
		}

		if (handleAgentStatus(data, ctx)) return;
		handleMessagesPayload(data, ctx);
		handleParticipantsPayload(data, ctx);
		if (handleJoinEvent(data, ctx)) return;
		if (handleQuitEvent(data, ctx)) return;
		if (data.action === WS_ACTIONS.ERROR) return;
		handleChatMessage(data, ctx, state.socket);
	};
}

/** Schedule a bounded exponential-backoff reconnect of `connect`. Shared by the socket `close` handler and the
 *  credential-not-yet-resolvable early-return in `initWebSocket` -- the latter previously returned silently, so the
 *  assistant never connected when `resolveWsCredential` was null on first mount. No-op once torn down or capped. */
function scheduleReconnect(state: WebSocketState, options: MessagingClientOptions, trigger: { connect: () => void; reason: string }): void {
	const { connect, reason } = trigger;
	if (state.closed || options.shouldReconnect === false) return;
	if (state.reconnectAttempts >= WEBSOCKET_RETRY_CONFIG.maxAttempts) {
		log.debug('Max reconnect attempts reached, stopping', {
			attempts: state.reconnectAttempts,
			maxAttempts: WEBSOCKET_RETRY_CONFIG.maxAttempts,
			reason,
		});
		return;
	}
	state.reconnectAttempts += 1;
	const delay = calculateBackoffWithJitter(state.reconnectAttempts - 1, WEBSOCKET_RETRY_CONFIG);
	log.debug('Scheduling WebSocket reconnect', { attempt: state.reconnectAttempts, delay, reason });
	state.reconnectTimeoutId = window.setTimeout(connect, delay);
}

function createCloseHandler(state: WebSocketState, options: MessagingClientOptions, connect: () => void): () => void {
	return () => {
		options.onConnectionChange?.(false);
		if (state.closed) return;
		state.socket = null;
		scheduleReconnect(state, options, { connect, reason: 'socket-close' });
	};
}

function createErrorHandler(state: WebSocketState): (event: Event) => void {
	return (event: Event) => {
		log.debug('WebSocket error occurred, closing connection', extractError(event));
		state.socket?.close();
	};
}

function removeSocketListeners(state: WebSocketState) {
	const ws = state.socket;
	if (ws) {
		if (state.handleOpen) ws.removeEventListener('open', state.handleOpen);
		if (state.handleMessage) ws.removeEventListener('message', state.handleMessage);
		if (state.handleClose) ws.removeEventListener('close', state.handleClose);
		if (state.handleError) ws.removeEventListener('error', state.handleError);
	}
	state.handleOpen = null;
	state.handleMessage = null;
	state.handleClose = null;
	state.handleError = null;
}

async function initWebSocket(
	state: WebSocketState,
	options: MessagingClientOptions,
	params: {
		config: ReturnType<typeof getEnvironmentConfig>;
		connect: () => void;
	},
) {
	const { config, connect } = params;
	const wsBase = options.wsUrlBase ?? config.chatRoomWS;
	// Present the REAL caller credential (Firebase ID token for authed users, the
	// persisted guest session token for guests) -- NEVER the public static token.
	//
	// Every RETRY forces a fresh mint. Without it a rejected token is re-presented
	// verbatim on each attempt, so a stale or expired one loops on "Reconnecting..."
	// until the user logs out and back in. The first attempt stays on the SDK cache
	// so the common path costs no extra round trip.
	const credential = await resolveWsCredential(options.userId, options.username, state.reconnectAttempts > 0, options.roomId);
	// The upgrade verifier requires the token's `gid` to equal the `userId` on the query string, so the subject is taken
	// FROM the credential rather than from app state. Those two disagree in a real, reachable case: the explore surface
	// mints its own server-side guest identity and writes it into the SAME app-state slot this pane reads, so a guest
	// whose chats token was signed for an earlier id then connects naming the explore id and is rejected -- while the
	// HTTP calls that ran before the overwrite still succeeded. Deriving it here makes the two structurally unable to
	// disagree, whichever surface wrote last. Unverified read: the worker still checks the signature.
	const guestSubject = credential ? readGuestSessionTokenClaimsUnverified(credential)?.gid : undefined;
	const wsUserId = guestSubject ?? options.userId;
	state.subjectUserId = wsUserId;
	const url = `${wsBase}${ROOM_API_ROUTES.PREFIX}${options.roomId}/websocket?userId=${encodeURIComponent(wsUserId)}&conversationId=${encodeURIComponent(options.roomId)}`;
	// The socket effect may have torn down while the credential was resolving.
	if (state.closed) return;
	if (!credential) {
		// The credential is not resolvable yet (Firebase currentUser unresolved, or a guest before the session token mints). Schedule a bounded retry -- a silent return here would leave the assistant permanently unconnected even after auth resolves. The static token is NEVER sent.
		log.debug('No WebSocket credential resolved yet; scheduling retry (static token is never sent)', {
			userId: options.userId,
		});
		scheduleReconnect(state, options, { connect, reason: 'awaiting-credential' });
		return;
	}
	state.socket = new WebSocket(url, buildClientSubprotocols(credential));
	state.handleOpen = createOpenHandler(state, options);
	state.handleMessage = createMessageHandler(state, options);
	state.handleClose = createCloseHandler(state, options, connect);
	state.handleError = createErrorHandler(state);
	state.socket.addEventListener('open', state.handleOpen);
	state.socket.addEventListener('message', state.handleMessage);
	state.socket.addEventListener('close', state.handleClose);
	state.socket.addEventListener('error', state.handleError);
}

function disconnectSocket(state: WebSocketState): void {
	state.closed = true;
	if (state.reconnectTimeoutId !== null) {
		window.clearTimeout(state.reconnectTimeoutId);
		state.reconnectTimeoutId = null;
	}
	removeSocketListeners(state);
	const socket = state.socket;
	state.socket = null;
	if (!socket) return;
	if (socket.readyState === WebSocket.CONNECTING) {
		// close() on a still-CONNECTING socket logs a closed-before-established warning, so defer it until the handshake completes.
		socket.addEventListener('open', () => socket.close(), { once: true });
	} else if (socket.readyState !== WebSocket.CLOSED) {
		socket.close();
	}
}

function buildMessagePayload(args: {
	options: MessagingClientOptions;
	content: string;
	files: FileAttachment[] | undefined;
	subjectUserId: string | null;
}): WsOutboundFrame {
	const { options, content, files, subjectUserId } = args;
	const payload: Record<string, unknown> = {
		name: options.username,
		userId: subjectUserId ?? options.userId,
		role: options.role,
		sendOnHandshake: true,
		message: content,
		conversationType: options.conversationType ?? DEFAULT_CONVERSATION_TYPE,
		roomId: options.roomId,
	};
	if (files && files.length > 0) {
		log.debug('Sending message with files', {
			fileCount: files.length,
			files: files.map((f) => ({ name: f.name, type: f.type })),
		});
		payload.files = files;
	}
	payload.type = 'chatMessage';
	return payload as WsOutboundFrame;
}

export function createMessagingClient(options: MessagingClientOptions): MessagingClientHandle {
	const config = getEnvironmentConfig();
	const state: WebSocketState = {
		socket: null,
		subjectUserId: null,
		closed: false,
		reconnectAttempts: 0,
		reconnectTimeoutId: null,
		handleOpen: null,
		handleMessage: null,
		handleClose: null,
		handleError: null,
	};

	function connect() {
		if (state.closed) return;
		void initWebSocket(state, options, { config, connect });
	}

	connect();

	return {
		disconnect: () => disconnectSocket(state),
		sendMessage(content: string, files?: FileAttachment[]) {
			if (!isWebSocketConnected(state.socket) || !state.socket) return false;
			return sendWsMessage(state.socket, buildMessagePayload({ options, content, files, subjectUserId: state.subjectUserId }));
		},
		isConnected: () => isWebSocketConnected(state.socket),
	};
}
